import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  LayoutDashboard,
  Scissors,
  Image,
  Star,
  CalendarDays,
  MessageSquare,
  LogOut,
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  Upload,
  Loader2,
  Bell,
  CheckCircle,
  Sparkles,
  Megaphone,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Send,
  Phone,
  Users,
  Mail,
  CalendarPlus,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { API, SITE_URL, authHeaders, resolveUrl, ImageUploadField } from "./shared";

const BeforeAfterTab = () => {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ client_name: "", service: "", before_image: "", after_image: "", description: "" });

  const fetchItems = useCallback(async () => {
    const res = await axios.get(`${API}/before-after`);
    setItems(res.data);
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleAdd = async () => {
    if (!form.client_name || !form.before_image || !form.after_image) {
      toast.error("Name and both images are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/before-after`, form, authHeaders());
      toast.success("Before/After added!");
      setShowAdd(false);
      setForm({ client_name: "", service: "", before_image: "", after_image: "", description: "" });
      fetchItems();
    } catch (err) {
      toast.error("Failed to add");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this before/after?")) return;
    try {
      await axios.delete(`${API}/admin/before-after/${id}`, authHeaders());
      toast.success("Deleted");
      fetchItems();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-before-after-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-brand-text">Before & After</h2>
        <button onClick={() => setShowAdd(!showAdd)} data-testid="add-ba-btn" className="flex items-center gap-2 px-4 py-2 bg-brand-gold text-black font-sans text-sm font-semibold hover:bg-brand-gold-light transition-colors">
          {showAdd ? <X size={14} /> : <Plus size={14} />}
          {showAdd ? "Cancel" : "Add Before/After"}
        </button>
      </div>

      {showAdd && (
        <div className="border border-brand-border bg-brand-surface p-5 mb-6 space-y-3" data-testid="ba-form">
          <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Client name" data-testid="ba-name" className="bg-brand-bg border-brand-border text-brand-text rounded-none" />
          <Input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} placeholder="Service done (e.g., Knotless Braids)" data-testid="ba-service" className="bg-brand-bg border-brand-border text-brand-text rounded-none" />
          <ImageUploadField currentUrl={form.before_image} onUrlChange={(url) => setForm({ ...form, before_image: url })} label="Before Photo" />
          <ImageUploadField currentUrl={form.after_image} onUrlChange={(url) => setForm({ ...form, after_image: url })} label="After Photo" />
          <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" data-testid="ba-desc" className="bg-brand-bg border-brand-border text-brand-text rounded-none" />
          <button onClick={handleAdd} data-testid="save-ba-btn" className="flex items-center gap-2 px-6 py-2 bg-brand-gold text-black font-sans text-sm font-semibold hover:bg-brand-gold-light transition-colors">
            <Save size={14} /> Add Before/After
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => (
          <div key={item.id} className="border border-brand-border overflow-hidden" data-testid={`admin-ba-${item.id}`}>
            <div className="grid grid-cols-2">
              <div className="relative">
                <img src={resolveUrl(item.before_image)} alt="Before" className="w-full aspect-square object-cover" />
                <span className="absolute bottom-1 left-1 font-sans text-[9px] uppercase bg-black/70 text-white px-1.5 py-0.5">Before</span>
              </div>
              <div className="relative">
                <img src={resolveUrl(item.after_image)} alt="After" className="w-full aspect-square object-cover" />
                <span className="absolute bottom-1 left-1 font-sans text-[9px] uppercase bg-brand-gold/90 text-black px-1.5 py-0.5">After</span>
              </div>
            </div>
            <div className="p-3 flex items-center justify-between">
              <div>
                <p className="font-sans text-sm text-brand-text">{item.client_name}</p>
                <p className="font-sans text-xs text-brand-gold">{item.service}</p>
              </div>
              <button onClick={() => handleDelete(item.id)} data-testid={`delete-ba-${item.id}`} className="p-1.5 border border-brand-border text-brand-text-muted hover:text-red-400 hover:border-red-400 transition-colors">
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BeforeAfterTab;
